import React from 'react';
import { Paper, Typography, Stack, Box, Chip, IconButton, Collapse, Tooltip, Grid } from '@mui/material';
import { 
  Event as EventIcon, 
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon, 
  LocationOn as LocationIcon, 
  Person as PersonIcon,
  Add as AddIcon,
  AddCircle as AddCircleIcon
} from '@mui/icons-material';
import schoolEvents from '../../data/school-events.json';

interface EventDisplayProps {
  categoryId: string;
  onAddEvent?: (categoryId: string) => void;
  onAddSubEvent?: (eventId: string) => void;
}

export const EventDisplay = React.forwardRef<HTMLDivElement, EventDisplayProps>(({
  categoryId,
  onAddEvent,
  onAddSubEvent
}, ref) => {
  const category = schoolEvents.categories.find(c => c.id === categoryId);
  const [expandedEvents, setExpandedEvents] = React.useState<Set<string>>(new Set());
  
  if (!category) {
    return null;
  }
  
  const handleToggleExpand = (eventId: string) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  return (
    <Paper 
      ref={ref} 
      sx={{ 
        height: '100%',
        overflow: 'auto',
        p: 3,
        scrollBehavior: 'smooth',
        '&::-webkit-scrollbar': {
          width: 8
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'background.default',
          borderRadius: 4
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'primary.light',
          borderRadius: 4,
          '&:hover': {
            bgcolor: 'primary.main'
          }
        }
      }}
    >
      <Stack spacing={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Typography variant="h5">
              {category.name}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton
              onClick={() => onAddEvent?.(categoryId)}
              color="primary"
              sx={{
                bgcolor: 'primary.lighter',
                '&:hover': {
                  bgcolor: 'primary.light'
                }
              }}
            >
              <AddCircleIcon />
            </IconButton>
          </Grid>
        </Grid>
        <Box>
          <Typography variant="body1" color="text.secondary">
            {category.description}
          </Typography>
        </Box>

        <Stack spacing={2}>
          {category.events.map((event) => (
            <Paper
              key={event.id}
              variant="outlined"
              sx={{
                p: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 2
                }
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {event.name}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {event.description}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {event.time}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {event.location}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {event.contactPerson}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    {event.subEvents && event.subEvents.length > 0 && (
                      <Tooltip title="View subevents">
                        <IconButton
                          size="small"
                          onClick={() => handleToggleExpand(event.id)}
                          sx={{
                            color: 'primary.main',
                            transition: 'transform 0.2s',
                            transform: expandedEvents.has(event.id) ? 'rotate(180deg)' : 'none'
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Add New Sub Event">
                      <IconButton
                        size="small"
                        onClick={() => onAddSubEvent?.(event.id)}
                        sx={{
                          color: 'primary.main',
                          bgcolor: 'primary.lighter',
                          '&:hover': {
                            bgcolor: 'primary.light'
                          }
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>

                {event.subEvents && event.subEvents.length > 0 && (
                  <Collapse in={expandedEvents.has(event.id)}>
                    <Box sx={{ pl: 4, pt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom color="text.secondary">
                        Related Events:
                      </Typography>
                      <Stack spacing={2}>
                        {event.subEvents.map((subEvent, index) => (
                          <Paper
                            key={index}
                            variant="outlined"
                            sx={{
                              p: 2,
                              bgcolor: 'background.default'
                            }}
                          >
                            <Typography variant="subtitle2" gutterBottom>
                              {subEvent.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {subEvent.description}
                            </Typography>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  </Collapse>
                )}
                {event.notes && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon fontSize="small" color="action" />
                    <Chip 
                      label={event.notes}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
});

EventDisplay.displayName = 'EventDisplay';